import React, { useState, useEffect } from 'react';
import { 
  Modal, Button, Form, Row, Col, Card, Alert, 
  Spinner, Badge, ProgressBar, InputGroup
} from 'react-bootstrap';
import { 
  FaMagic, FaHome, FaDollarSign, FaCalendar, 
  FaRobot, FaCheckCircle, FaRocket, FaBolt,
  FaStar, FaClock, FaShieldAlt, FaUserTie,
  FaChartLine, FaLightbulb, FaArrowRight,
  FaExclamationTriangle, FaUsers, FaEdit,
  FaFileSignature
} from 'react-icons/fa';
import axiosInstance from '../../helpers/api';
import './MakeOfferModal.css';

interface MakeOfferModalProps {
  show: boolean;
  onHide: () => void;
  transaction: any;
  onOfferCreated: (offerId: string) => void;
}

const MakeOfferModal: React.FC<MakeOfferModalProps> = ({
  show, onHide, transaction, onOfferCreated
}) => {
  const [loading, setLoading] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [aiProgress, setAiProgress] = useState(0);
  const [aiMessage, setAiMessage] = useState('');
  const [propertyData, setPropertyData] = useState<any>(null);
  const [aiInsights, setAiInsights] = useState<any>(null);
  const [showPartyWarning, setShowPartyWarning] = useState(false);
  const [pendingDocuments, setPendingDocuments] = useState<any[]>([]);
  const [documentStatus, setDocumentStatus] = useState<any>({});
  const [buyerInfo, setBuyerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  // Simplified offer terms - only what agent needs to input
  const [offerTerms, setOfferTerms] = useState({
    offer_price: '',
    earnest_money: '',
    closing_date: '',
    special_requests: ''
  });
  
  const [validation, setValidation] = useState<any>({});
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (show) {
      // Reset states when modal opens
      setShowPartyWarning(false);
      setAiProcessing(false);
      setCurrentStep(1); // Start at step 1
      setPropertyData(null);
      setAiInsights(null);
      setPendingDocuments([]);
      
      // Check if parties are defined
      const hasBuyer = transaction.parties?.some((p: any) => p.party_type === 'buyer');
      
      if (!hasBuyer) {
        // Show party warning
        setShowPartyWarning(true);
      } else {
        // Check for pending documents
        checkPendingDocuments();
      }
    }
  }, [show]);

  const checkPendingDocuments = async () => {
    try {
      // Get documents that need signing
      const unsignedDocs = transaction.documents?.filter((doc: any) => 
        doc.status === 'pending' || doc.status === 'draft' || !doc.status
      ) || [];
      
      setPendingDocuments(unsignedDocs);
    } catch (error) {
      console.error('Error checking documents:', error);
    }
  };

  const handleDocumentPrefill = async () => {
    setAiProcessing(true);
    setCurrentStep(2);
    setAiMessage('🤖 Pre-filling buyer documents with AI...');
    
    try {
      // Pre-fill all pending documents with buyer information
      const buyer = transaction.parties?.find((p: any) => p.party_type === 'buyer');
      
      for (const doc of pendingDocuments) {
        setAiMessage(`📄 Processing ${doc.name || doc.filename}...`);
        setAiProgress((pendingDocuments.indexOf(doc) + 1) / pendingDocuments.length * 100);
        
        // Call Anvil pre-fill endpoint
        const response = await axiosInstance.post(`/api/transaction-prefill/${transaction.id}/fill`, {
          documentId: doc.id,
          partyData: {
            buyer_name: buyer.name,
            buyer_email: buyer.email,
            buyer_phone: buyer.phone,
            buyer_address: buyer.address || '',
            contract_date: new Date().toISOString().split('T')[0],
            property_address: transaction.property?.address || transaction.property_address
          }
        });
        
        if (response.data.success) {
          setDocumentStatus((prev: any) => ({
            ...prev,
            [doc.id]: 'prefilled'
          }));
        }
        
        // Small delay for UI feedback
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      setAiMessage('✅ All documents pre-filled! Sending to customer portal...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Send to customer portal
      await sendDocumentsToPortal();
      
    } catch (error) {
      console.error('Error pre-filling documents:', error);
    } finally {
      setAiProcessing(false);
      setCurrentStep(3);
    }
  };

  const sendDocumentsToPortal = async () => {
    try {
      const buyer = transaction.parties?.find((p: any) => p.party_type === 'buyer');
      
      const response = await axiosInstance.post(`/api/transactions/${transaction.id}/send-for-signatures`, {
        documents: pendingDocuments.map(doc => doc.id),
        signers: [{
          email: buyer.email,
          name: buyer.name,
          role: 'BUYER'
        }]
      });
      
      if (response.data.success) {
        setAiMessage('📧 Documents sent to buyer for signature!');
      }
    } catch (error) {
      console.error('Error sending documents:', error);
    }
  };

  const startAiMagic = async () => {
    setAiProcessing(true);
    setCurrentStep(2); // Step 2 is AI processing
    
    // Simulate AI progress with engaging messages
    const aiSteps = [
      { progress: 10, message: "🏠 Analyzing property details..." },
      { progress: 25, message: "📊 Reviewing market comparables..." },
      { progress: 40, message: "🔍 Evaluating neighborhood trends..." },
      { progress: 60, message: "💡 Generating strategic insights..." },
      { progress: 80, message: "✨ Preparing your competitive advantage..." },
      { progress: 100, message: "🎯 Ready to create your winning offer!" }
    ];

    // Animate through AI steps
    for (const step of aiSteps) {
      setAiProgress(step.progress);
      setAiMessage(step.message);
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    try {
      // Load property data and AI insights
      const response = await axiosInstance.post('/api/offers/ai-analyze', {
        transaction_id: transaction.id,
        property_id: transaction.property_id
      });

      if (response.data.success) {
        setPropertyData(response.data.property);
        setAiInsights(response.data.insights);
        
        // Pre-fill AI suggestions
        setOfferTerms(prev => ({
          ...prev,
          offer_price: response.data.insights.suggested_price?.toString() || ''
        }));
      }
    } catch (error) {
      console.error('Error loading AI insights:', error);
    } finally {
      setAiProcessing(false);
      setCurrentStep(3); // Step 3 is offer terms
    }
  };

  const formatPrice = (value: string) => {
    // Remove non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    // Format with commas
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handlePriceChange = (value: string) => {
    const formatted = formatPrice(value);
    setOfferTerms({ ...offerTerms, offer_price: formatted });
  };

  const calculatePercentage = (offer: string, list: number) => {
    const offerNum = parseInt(offer.replace(/[^0-9]/g, ''));
    if (!offerNum || !list) return '0';
    return ((offerNum / list) * 100).toFixed(1);
  };

  const handleGenerateOffer = async () => {
    // Simple validation
    const errors: any = {};
    if (!offerTerms.offer_price) errors.offer_price = 'Required';
    if (!offerTerms.earnest_money) errors.earnest_money = 'Required';
    if (!offerTerms.closing_date) errors.closing_date = 'Required';
    
    if (Object.keys(errors).length > 0) {
      setValidation(errors);
      return;
    }
    
    setLoading(true);
    setCurrentStep(4); // Step 4 is generating offer
    
    try {
      const response = await axiosInstance.post('/api/offers/generate-with-ai', {
        transaction_id: transaction.id,
        offer_terms: {
          ...offerTerms,
          offer_price: offerTerms.offer_price.replace(/[^0-9]/g, ''), // Clean price
          earnest_money: offerTerms.earnest_money.replace(/[^0-9]/g, '')
        },
        property_data: propertyData,
        ai_insights: aiInsights
      });
      
      if (response.data.success) {
        setShowSuccess(true);
        setTimeout(() => {
          onOfferCreated(response.data.offer_id);
          onHide();
        }, 2000);
      }
    } catch (error) {
      console.error('Error generating offer:', error);
      alert('Failed to generate offer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getClosingDateSuggestion = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30); // Default 30 days
    return date.toISOString().split('T')[0];
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static" className="make-offer-modal">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="w-100">
          <div className="d-flex align-items-center justify-content-between w-100">
            <div className="d-flex align-items-center">
              <div className="ai-icon-wrapper me-3">
                <FaRobot className="ai-icon" />
              </div>
              <div>
                <h4 className="mb-0">AI-Powered Offer Assistant</h4>
                <small className="text-muted">Let's create a winning offer together!</small>
              </div>
            </div>
            {currentStep > 0 && (
              <Badge bg="primary" className="px-3 py-2">
                Step {currentStep} of 4
              </Badge>
            )}
          </div>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="px-4">
        {/* Step 1: Party Confirmation/Addition */}
        {currentStep === 1 && !aiProcessing && (
          <div className="party-step">
            {showPartyWarning ? (
              // No buyer - show warning and form
              <div className="party-warning-section">
            <Alert variant="warning" className="mb-4">
              <div className="d-flex align-items-start">
                <FaExclamationTriangle className="me-3 mt-1 flex-shrink-0" size={24} />
                <div>
                  <h6 className="mb-2">Buyer Information Required</h6>
                  <p className="mb-0">
                    To create an offer, we need the buyer's information. You can quickly add it below, 
                    or go to the Parties tab to add complete information for all parties.
                  </p>
                </div>
              </div>
            </Alert>

            <Card className="quick-buyer-form">
              <Card.Body>
                <h6 className="mb-4 d-flex align-items-center">
                  <FaUserTie className="me-2 text-primary" />
                  Quick Buyer Information
                </h6>
                
                <Form>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Buyer Name*</Form.Label>
                        <Form.Control
                          type="text"
                          value={buyerInfo.name}
                          onChange={(e) => setBuyerInfo({...buyerInfo, name: e.target.value})}
                          placeholder="John and Jane Smith"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email*</Form.Label>
                        <Form.Control
                          type="email"
                          value={buyerInfo.email}
                          onChange={(e) => setBuyerInfo({...buyerInfo, email: e.target.value})}
                          placeholder="buyer@email.com"
                        />
                        <Form.Text className="text-muted">
                          For sending offer documents
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="tel"
                          value={buyerInfo.phone}
                          onChange={(e) => setBuyerInfo({...buyerInfo, phone: e.target.value})}
                          placeholder="(555) 123-4567"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <Button 
                      variant="outline-primary"
                      onClick={() => {
                        onHide();
                        // Navigate to parties tab
                        window.location.hash = '#parties';
                      }}
                    >
                      <FaUsers className="me-2" />
                      Add All Parties Instead
                    </Button>
                    
                    <Button
                      variant="primary"
                      onClick={() => {
                        if (buyerInfo.name && buyerInfo.email) {
                          // Add buyer to transaction parties
                          transaction.parties = transaction.parties || [];
                          transaction.parties.push({
                            id: `temp_${Date.now()}`,
                            party_type: 'buyer',
                            name: buyerInfo.name,
                            email: buyerInfo.email,
                            phone: buyerInfo.phone,
                            is_primary: true
                          });
                          setShowPartyWarning(false);
                          startAiMagic();
                        } else {
                          alert('Please enter buyer name and email');
                        }
                      }}
                      disabled={!buyerInfo.name || !buyerInfo.email}
                    >
                      <FaArrowRight className="me-2" />
                      Continue to Offer
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </div>
            ) : (
              // Buyer exists - show confirmation
              <div className="party-confirmation">
                <Card className="mb-4">
                  <Card.Body>
                    <h6 className="mb-3 d-flex align-items-center">
                      <FaCheckCircle className="me-2 text-success" />
                      Transaction Parties
                    </h6>
                    
                    <div className="party-list">
                      {transaction.parties?.map((party: any) => (
                        <div key={party.id} className="party-item mb-2 p-2 bg-light rounded">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{party.name}</strong>
                              <Badge bg="primary" className="ms-2">{party.party_type}</Badge>
                            </div>
                            <small className="text-muted">{party.email}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {pendingDocuments.length > 0 && (
                      <Alert variant="warning" className="mt-3 mb-3">
                        <FaExclamationTriangle className="me-2" />
                        <strong>{pendingDocuments.length} documents need signatures!</strong>
                        <p className="mb-1 mt-2">The following documents are ready for the buyer:</p>
                        <ul className="mb-2">
                          {pendingDocuments.map(doc => (
                            <li key={doc.id}>{doc.name || doc.filename || doc.original_filename}</li>
                          ))}
                        </ul>
                        <small>We can pre-fill these with AI and send them for signature first.</small>
                      </Alert>
                    )}
                    
                    {pendingDocuments.length === 0 && (
                      <Alert variant="info" className="mt-3 mb-3">
                        <FaLightbulb className="me-2" />
                        <strong>Ready to proceed?</strong> Our AI will analyze the property and help you create a competitive offer.
                      </Alert>
                    )}
                    
                    <div className="d-flex justify-content-between">
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          onHide();
                          // Navigate to parties tab to edit
                          window.location.hash = '#parties';
                        }}
                      >
                        <FaEdit className="me-2" />
                        Edit Parties
                      </Button>
                      
                      {pendingDocuments.length > 0 ? (
                        <div className="d-flex gap-2">
                          <Button
                            variant="warning"
                            size="lg"
                            onClick={() => handleDocumentPrefill()}
                            className="px-4"
                          >
                            <FaFileSignature className="me-2" />
                            Pre-fill & Send Documents
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="lg"
                            onClick={() => startAiMagic()}
                            className="px-3"
                          >
                            <FaArrowRight className="me-2" />
                            Skip to Offer
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="primary"
                          size="lg"
                          onClick={() => startAiMagic()}
                          className="px-4"
                        >
                          <FaMagic className="me-2" />
                          Start AI Analysis
                        </Button>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* AI Processing Animation */}
        {aiProcessing && (
          <div className="ai-processing-section text-center py-5">
            <div className="ai-animation mb-4">
              <FaMagic className="magic-icon spinning" />
            </div>
            <ProgressBar 
              animated 
              now={aiProgress} 
              variant="primary" 
              className="mb-3"
              style={{ height: '8px' }}
            />
            <h5 className="text-primary mb-2">{aiMessage}</h5>
            <p className="text-muted">Our AI is working its magic to help you craft the perfect offer...</p>
          </div>
        )}

        {/* Property Overview with AI Insights */}
        {!aiProcessing && propertyData && (
          <>
            {/* Property Card */}
            <Card className="property-highlight-card mb-4">
              <Card.Body>
                <Row>
                  <Col md={8}>
                    <h5 className="mb-2 d-flex align-items-center">
                      <FaHome className="me-2 text-primary" />
                      {propertyData.address}
                    </h5>
                    <div className="property-details">
                      <Badge bg="light" text="dark" className="me-2">
                        {propertyData.bedrooms} BR / {propertyData.bathrooms} BA
                      </Badge>
                      <Badge bg="light" text="dark" className="me-2">
                        {propertyData.sqft?.toLocaleString()} sqft
                      </Badge>
                      <Badge bg="light" text="dark">
                        Built {propertyData.yearBuilt}
                      </Badge>
                    </div>
                  </Col>
                  <Col md={4} className="text-end">
                    <div className="price-display">
                      <small className="text-muted">List Price</small>
                      <h4 className="mb-0 text-primary">${propertyData.price?.toLocaleString()}</h4>
                      <small className="text-success">
                        <FaClock className="me-1" />
                        {propertyData.daysOnMarket} days on market
                      </small>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* AI Insights Card */}
            {aiInsights && (
              <Card className="ai-insights-card mb-4">
                <Card.Body>
                  <h6 className="d-flex align-items-center mb-3">
                    <FaLightbulb className="me-2 text-warning" />
                    AI Market Intelligence
                  </h6>
                  <Row>
                    <Col md={4}>
                      <div className="insight-item">
                        <FaChartLine className="insight-icon text-success" />
                        <div>
                          <small className="text-muted">Market Trend</small>
                          <p className="mb-0 fw-bold">{aiInsights.market_trend}</p>
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="insight-item">
                        <FaDollarSign className="insight-icon text-primary" />
                        <div>
                          <small className="text-muted">Suggested Offer</small>
                          <p className="mb-0 fw-bold">${aiInsights.suggested_price?.toLocaleString()}</p>
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="insight-item">
                        <FaBolt className="insight-icon text-warning" />
                        <div>
                          <small className="text-muted">Competition</small>
                          <p className="mb-0 fw-bold">{aiInsights.competition_level}</p>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  {aiInsights.key_recommendation && (
                    <Alert variant="info" className="mt-3 mb-0">
                      <FaStar className="me-2" />
                      <strong>AI Recommendation:</strong> {aiInsights.key_recommendation}
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Simple Offer Form */}
            <Card className="offer-form-card">
              <Card.Body>
                <h6 className="mb-4">Your Offer Details</h6>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">
                        <FaDollarSign className="me-2 text-primary" />
                        Offer Price
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control
                          type="text"
                          value={offerTerms.offer_price}
                          onChange={(e) => handlePriceChange(e.target.value)}
                          placeholder="Enter your offer"
                          isInvalid={!!validation.offer_price}
                          className="form-control-lg"
                        />
                      </InputGroup>
                      {offerTerms.offer_price && propertyData?.price && (
                        <div className="price-feedback mt-2">
                          <small className={`text-${parseFloat(calculatePercentage(offerTerms.offer_price, propertyData.price)) >= 95 ? 'success' : 'warning'}`}>
                            {calculatePercentage(offerTerms.offer_price, propertyData.price)}% of list price
                          </small>
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">
                        <FaShieldAlt className="me-2 text-primary" />
                        Earnest Money
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control
                          type="text"
                          value={offerTerms.earnest_money}
                          onChange={(e) => setOfferTerms({...offerTerms, earnest_money: formatPrice(e.target.value)})}
                          placeholder="Typically 1-3% of offer"
                          isInvalid={!!validation.earnest_money}
                          className="form-control-lg"
                        />
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Shows you're serious about the purchase
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">
                        <FaCalendar className="me-2 text-primary" />
                        Desired Closing Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={offerTerms.closing_date}
                        onChange={(e) => setOfferTerms({...offerTerms, closing_date: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                        isInvalid={!!validation.closing_date}
                        className="form-control-lg"
                      />
                      <Form.Text className="text-muted">
                        Typically 30-45 days from acceptance
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">
                        <FaUserTie className="me-2 text-primary" />
                        Special Requests (Optional)
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={offerTerms.special_requests}
                        onChange={(e) => setOfferTerms({...offerTerms, special_requests: e.target.value})}
                        placeholder="Any special conditions or requests?"
                        className="form-control"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* AI Notice */}
                <Alert variant="light" className="ai-notice">
                  <FaRobot className="me-2 text-primary" />
                  <strong>AI will handle the rest!</strong> We'll automatically fill in escrow details, 
                  standard contingencies, and all legal requirements based on local practices.
                </Alert>
              </Card.Body>
            </Card>
          </>
        )}

        {/* Document Success View */}
        {currentStep === 3 && documentStatus && Object.keys(documentStatus).length > 0 && !propertyData && (
          <div className="document-success-view text-center py-5">
            <div className="success-icon-wrapper mb-4">
              <FaCheckCircle className="success-icon" />
            </div>
            <h4 className="text-success mb-3">Documents Sent Successfully!</h4>
            <p className="text-muted mb-4">
              {pendingDocuments.length} document{pendingDocuments.length > 1 ? 's' : ''} have been pre-filled and sent to {transaction.parties?.find((p: any) => p.party_type === 'buyer')?.name} for signature.
            </p>
            
            <div className="d-flex justify-content-center gap-3">
              <Button 
                variant="outline-secondary" 
                onClick={onHide}
              >
                Close
              </Button>
              <Button 
                variant="primary"
                onClick={() => {
                  // Reset for offer creation
                  setCurrentStep(1);
                  setPendingDocuments([]);
                  setDocumentStatus({});
                  startAiMagic();
                }}
              >
                <FaArrowRight className="me-2" />
                Continue to Make Offer
              </Button>
            </div>
          </div>
        )}

        {/* Success Animation */}
        {showSuccess && (
          <div className="success-animation text-center py-5">
            <div className="success-icon-wrapper mb-4">
              <FaCheckCircle className="success-icon" />
            </div>
            <h4 className="text-success mb-2">Offer Generated Successfully!</h4>
            <p className="text-muted">Sending to {transaction.client_name} for review...</p>
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer className="border-0">
        <Button variant="light" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        {!aiProcessing && !showSuccess && (
          <Button 
            variant="primary" 
            onClick={handleGenerateOffer}
            disabled={loading}
            size="lg"
            className="px-4"
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Creating Your Offer...
              </>
            ) : (
              <>
                <FaRocket className="me-2" />
                Generate & Send Offer
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default MakeOfferModal;